import { Translation } from '@/app/i18n';
import { fetchCategoryItem, fetchCategoryList } from '@/app/modules/ResourceLibrary/network/resourceCrud';
import { CategoryItem, CategoryLevel, CategoryPaginateList } from '@/app/modules/ResourceLibrary/types';
import Form from '@common/components/Form';
import { SubdirectoryArrowRight } from '@mui/icons-material';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import Select from 'react-select';
import { useStyles } from './styles';
import { FormInstance } from 'rc-field-form';

interface OptionItem {
  title: string | ReactNode;
  value: string;
  children?: OptionItem[];
}

interface SelectOption {
  label: string | ReactNode;
  value: string;
}

export const CategoryFormItem = ({
  disabled,
  moduleType,
  form,
}: {
  disabled: boolean;
  moduleType: string;
  form: FormInstance<any>;
}) => {
  const { classes } = useStyles();
  const [options, setOptions] = useState<OptionItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const category = (form.getFieldValue('category') as SelectOption) ?? null;

  const getChildrenData = async (category: CategoryItem): Promise<OptionItem[]> => {
    if (!category.children?.count) {
      return [];
    }

    const parentDetail = await fetchCategoryItem(category.id);

    if (!parentDetail?.children?.length) {
      return [];
    }

    const level2Details = await Promise.all(parentDetail.children.map((child: any) => fetchCategoryItem(child.id)));

    return level2Details.map((level2Item) => ({
      level: CategoryLevel.TWO,
      title: level2Item.name,
      value: level2Item.id,
      children: level2Item.children?.length
        ? level2Item.children.map((child: any) => ({
            level: CategoryLevel.THREE,
            title: child.name,
            value: child.id,
          }))
        : undefined,
    }));
  };

  const appendChildrenDataToList = async (originalPaginateList: CategoryPaginateList): Promise<OptionItem[]> => {
    const rootCategories = originalPaginateList.categoryList.filter((item) => !item.parent);

    const level2Lists = await Promise.all(rootCategories.map(getChildrenData));

    return rootCategories.map((category, index) => ({
      level: CategoryLevel.ONE,
      title: category.name,
      value: category.id,
      children: level2Lists[index].length > 0 ? level2Lists[index] : undefined,
    }));
  };

  const getCategoryOptionList = async () => {
    setLoading(true);
    try {
      const categoryList = await fetchCategoryList({
        module: moduleType,
        page: 1,
        pageSize: 1000,
      });
      const optionList = await appendChildrenDataToList(categoryList);

      setOptions(optionList);
    } catch (error) {
      console.error('Failed to load category options', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const flattenTreeOptions = (treeOption: OptionItem, level = 0, result: SelectOption[] = []): SelectOption[] => {
    result.push({
      label: (
        <span className="tree-select-option" style={{ marginLeft: 18 * level }}>
          {level === 0 ? (
            <>{treeOption.title}</>
          ) : (
            <>
              <SubdirectoryArrowRight></SubdirectoryArrowRight> {treeOption.title}
            </>
          )}
        </span>
      ),
      value: treeOption.value,
    });
    if (treeOption.children) {
      treeOption.children.forEach((child) => {
        flattenTreeOptions(child, level + 1, result);
      });
    }
    return result;
  };

  const selectOptions = useMemo(() => {
    const flattenedOptions = options.flatMap((option) => flattenTreeOptions(option));
    if (category) {
      const hasCategory = flattenedOptions.some((option) => option.value === category.value);
      if (!hasCategory) {
        return [
          {
            label: <span className="tree-select-option">{category.label}</span>,
            value: category.value,
          },
          ...flattenedOptions,
        ];
      }
      return flattenedOptions;
    }
    return flattenedOptions;
  }, [options, category, flattenTreeOptions]);

  useEffect(() => {
    getCategoryOptionList();
  }, []);

  return (
    <Form.Item
      childStyle={{ marginTop: 16, height: 32 }}
      name="category"
      label={Translation('component.formLabel.category')}
      required
      rules={[{ required: true, message: Translation('component.form-required-text') }]}
    >
      {({ value, onChange }) => {
        return (
          <div className={`${classes.d_flex_column} ${classes.w_50}`}>
            <Select<SelectOption>
              isDisabled={disabled || loading}
              isLoading={loading}
              isSearchable={false}
              options={selectOptions}
              closeMenuOnSelect={true}
              value={value}
              onChange={onChange}
              placeholder="Please select"
              styles={{
                singleValue: (provided) => ({
                  ...provided,
                  color: '#333',
                }),
                option: (provided) => ({
                  ...provided,
                  color: '#333',
                }),
              }}
              theme={(theme) => ({
                ...theme,
                colors: {
                  ...theme.colors,
                  primary25: 'rgba(0, 0, 0, 0.04)',
                  primary: 'rgba(0, 0, 0, 0.1)',
                },
              })}
            />
          </div>
        );
      }}
    </Form.Item>
  );
};
